import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
    Search,
    Bell,
    User as UserIcon,
    Settings,
    LogOut,
    Clock,
    Printer,
    CheckCircle2
} from 'lucide-react';
import { useAuthStore } from '@/app/store/authStore';
import { ProfilePopup } from '@/app/pages/profile/components/ProfilePopup';
import { useNavigate } from 'react-router-dom';
import { BasicPrinterSettings } from '@/app/components/BasicPrinterSettings';
import { SyncStatus } from '@/app/components/SyncStatus';
import { OrganizationSwitcherBadge } from '@/app/components/layout/OrganizationSwitcherBadge';
import { OrganizationSwitcherModal } from '@/app/components/layout/OrganizationSwitcherModal';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { taskService } from '@/app/services/taskService';

interface HeaderProps {
    isMobile?: boolean;
}

const Header: React.FC<HeaderProps> = ({ isMobile }) => {
    const { logoutAction, user } = useAuthStore();
    const navigate = useNavigate();
    const [isProfilePopupOpen, setIsProfilePopupOpen] = useState(false);
    const [isPrinterSettingsOpen, setIsPrinterSettingsOpen] = useState(false);
    const [isOrgSwitcherOpen, setIsOrgSwitcherOpen] = useState(false);

    const handleLogout = (): void => {
        logoutAction();
    };

    const handleProfileClick = (): void => {
        setIsProfilePopupOpen(true);
    };

    // Get initials for Avatar Fallback
    const getInitials = (name?: string | null): string => {
        if (!name) return 'U';
        const nameParts = name.split(' ');
        if (nameParts.length === 1) return nameParts[0].charAt(0).toUpperCase();
        return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
    };
    const userInitials = getInitials(user?.full_name || user?.username);

    // Fetch task counts by status with 30-second refresh interval
    const { data: taskCounts } = useQuery({
        queryKey: ['taskCounts'],
        queryFn: async () => {
            const [openTasks, completedTasks] = await Promise.all([
                taskService.list({ status: 'open' as any, limit: 1, skip: 0 }),
                taskService.list({ status: 'completed' as any, limit: 1, skip: 0 }),
            ]);

            return {
                open: openTasks.total || 0,
                completed: completedTasks.total || 0,
                total: (openTasks.total || 0) + (completedTasks.total || 0),
            };
        },
        refetchInterval: 30000, // Refresh every 30 seconds
        staleTime: 25000,
    });

    return (
        <>
            <header className={cn(
                "sticky top-0 z-30 border-b bg-background",
                "px-4 py-2 sm:px-6 sm:py-3",
                isMobile && "pt-0" // Less padding on mobile when menu button is above
            )}>
                {/* Top row with search and user actions */}
                <div className="flex items-center gap-2">
                    <div className={cn(
                        "relative flex-1",
                        "max-w-full sm:max-w-[300px] md:max-w-[400px]"
                    )}>
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            type="search"
                            placeholder={isMobile ? "Search..." : "Search tasks, parties, invoices..."}
                            className="w-full rounded-lg bg-muted pl-8 text-sm sm:text-base"
                        />
                    </div>

                    {/* Task Status Counters - Desktop only */}
                    <div className="hidden sm:flex items-center gap-2">
                        <Badge
                            variant="outline"
                            className="gap-1 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950 dark:text-blue-300 border-blue-200 dark:border-blue-800 cursor-pointer"
                            onClick={() => navigate('/tasks?status=open')}
                        >
                            <Clock className="h-3 w-3" />
                            <span className="text-xs font-medium">Open:</span>
                            <span className="text-xs font-bold">{taskCounts?.open || 0}</span>
                        </Badge>

                        <Badge
                            variant="outline"
                            className="gap-1 bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-950 dark:text-green-300 border-green-200 dark:border-green-800 cursor-pointer"
                            onClick={() => navigate('/tasks?status=completed')}
                        >
                            <CheckCircle2 className="h-3 w-3" />
                            <span className="text-xs font-medium">Completed:</span>
                            <span className="text-xs font-bold">{taskCounts?.completed || 0}</span>
                        </Badge>
                    </div>

                    <div className="hidden sm:block h-8 w-px bg-border" />
                    
                    {/* Recent Documents - Hidden on mobile */}
                    <div className="hidden sm:block">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="gap-1">
                                    <Clock className="h-4 w-4" />
                                    <span className="hidden md:inline">Recent</span>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-[300px]">
                                <DropdownMenuLabel>Recent Documents</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem className="text-sm text-muted-foreground">
                                    No recent documents
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                    
                    {/* Sync Status - Hidden on small mobile */}
                    <div className="hidden sm:block">
                        <SyncStatus />
                    </div>

                    {/* Organization Switcher - Only visible for multi-org users */}
                    <OrganizationSwitcherBadge onClick={() => setIsOrgSwitcherOpen(true)} />

                    {/* Notifications */}
                    <Button variant="ghost" size="icon" className="relative">
                        <Bell className="h-4 w-4" />
                        <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-red-500" />
                        <span className="sr-only">Notifications</span>
                    </Button>
                    
                    {/* User Profile */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="rounded-full">
                                <Avatar className="h-8 w-8">
                                    <AvatarImage src="/placeholder-user.jpg" alt={user?.username} />
                                    <AvatarFallback>{userInitials}</AvatarFallback>
                                </Avatar>
                                <span className="sr-only">Toggle user menu</span>
                            </Button>
                        </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onSelect={handleProfileClick} className="cursor-pointer">
                                    <UserIcon className="mr-2 h-4 w-4" />
                                    <span>Profile</span>
                                </DropdownMenuItem>
                                <DropdownMenuItem>
                                    <Settings className="mr-2 h-4 w-4" />
                                    <span>Settings</span>
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                    onSelect={(e) => {
                                        e.preventDefault();
                                        setIsPrinterSettingsOpen(true);
                                    }} 
                                    className="cursor-pointer"
                                >
                                    <Printer className="mr-2 h-4 w-4" />
                                    <span>Printer Settings</span>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                                    <LogOut className="mr-2 h-4 w-4" />
                                    <span>Logout</span>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </header>
            
            {/* Profile Popup */}
            <ProfilePopup
                isOpen={isProfilePopupOpen}
                onOpenChange={setIsProfilePopupOpen}
            />
            
            {/* Printer Settings */}
            <BasicPrinterSettings
                isOpen={isPrinterSettingsOpen}
                onClose={() => setIsPrinterSettingsOpen(false)}
            />

            {/* Organization Switcher Modal */}
            <OrganizationSwitcherModal
                isOpen={isOrgSwitcherOpen}
                onOpenChange={setIsOrgSwitcherOpen}
            />
        </>
    );
};

export default Header; 